import { DEFAULT_ONLINE_FIELD, fieldSeatsForMode, type OnlineModeId, type OnlineRoom } from "@golazo/shared";
import type { OnlineClient } from "./client.js";
import type { RoomStore } from "./rooms.js";

export interface NetworkStoreOptions {
  client: OnlineClient;
}

export function createNetworkRoomStore(options: NetworkStoreOptions): RoomStore {
  const { client } = options;
  let rooms: OnlineRoom[] = [];
  const listeners = new Set<(rooms: OnlineRoom[]) => void>();

  const emit = (): void => {
    const snapshot = rooms.map((r) => ({ ...r, seats: { ...r.seats } }));
    for (const fn of listeners) fn(snapshot);
  };

  const pendingForHost = (): OnlineRoom[] =>
    rooms.filter((r) => r.id.startsWith("pending-") && r.hostId === client.clientId);

  const dropPendingForHost = (hostId: string): void => {
    rooms = rooms.filter((r) => !(r.id.startsWith("pending-") && r.hostId === hostId));
  };

  const upsertRoom = (room: OnlineRoom): void => {
    if (room.hostId) dropPendingForHost(room.hostId);
    const idx = rooms.findIndex((r) => r.id === room.id);
    if (idx >= 0) rooms[idx] = { ...room, seats: { ...room.seats } };
    else rooms = [{ ...room, seats: { ...room.seats } }, ...rooms];
    rooms = rooms.filter((r) => r.status === "lobby" || r.status === "playing");
    emit();
  };

  const applySync = (synced: OnlineRoom[]): void => {
    const pending = pendingForHost();
    rooms = [
      ...pending.filter(
        (p) => !synced.some((r) => r.hostId === p.hostId && r.name === p.name),
      ),
      ...synced.map((r) => ({
        ...r,
        visibility: r.visibility ?? "public",
        fieldId: r.fieldId ?? DEFAULT_ONLINE_FIELD,
        seats: { ...r.seats },
      })),
    ];
    rooms = rooms.filter((r) => r.status === "lobby" || r.status === "playing");
    emit();
  };

  const unsubMsg = client.onMessage((msg) => {
    switch (msg.type) {
      case "rooms:sync":
        applySync(msg.rooms);
        break;
      case "room:update":
        upsertRoom(msg.room);
        break;
      case "match:start":
        upsertRoom({
          // Lo que la sala ya sabia (banquillo, formato, visibilidad) se
          // conserva: `match:start` solo trae lo que hace falta para simular.
          ...(rooms.find((r) => r.id === msg.roomId) ?? {}),
          id: msg.roomId,
          name: rooms.find((r) => r.id === msg.roomId)?.name ?? msg.roomId,
          modeId: msg.modeId,
          hostId: rooms.find((r) => r.id === msg.roomId)?.hostId ?? "",
          seats: msg.seats,
          status: "playing",
          createdAt: rooms.find((r) => r.id === msg.roomId)?.createdAt ?? Date.now(),
          visibility: rooms.find((r) => r.id === msg.roomId)?.visibility ?? "public",
          fieldId: rooms.find((r) => r.id === msg.roomId)?.fieldId ?? DEFAULT_ONLINE_FIELD,
        });
        break;
      case "room:kicked":
        // Fuera de la sala: se deja de pintar como tuya hasta el siguiente sync.
        rooms = rooms.filter((r) => r.id !== msg.roomId);
        emit();
        break;
      case "error":
        if (msg.code === "create_failed") {
          dropPendingForHost(client.clientId);
          emit();
        }
        break;
    }
  });

  return {
    get clientId() {
      return client.clientId;
    },

    subscribe(fn) {
      listeners.add(fn);
      fn(rooms.map((r) => ({ ...r, seats: { ...r.seats } })));
      return () => listeners.delete(fn);
    },

    list() {
      return rooms.map((r) => ({ ...r, seats: { ...r.seats } }));
    },

    refresh() {
      client.send({ type: "room:list" });
    },

    create(opts) {
      const name = opts.name.trim() || `Sala ${opts.modeId}`;
      dropPendingForHost(client.clientId);
      const visibility = opts.visibility ?? "public";
      const optimistic: OnlineRoom = {
        id: `pending-${Date.now()}`,
        name,
        modeId: opts.modeId,
        hostId: client.clientId,
        seats: {
          [fieldSeatsForMode(opts.modeId)[0]?.playerId ?? "p0"]: {
            clientId: client.clientId,
            nickname: opts.nickname,
          },
        },
        status: "lobby",
        createdAt: Date.now(),
        visibility,
        fieldId: opts.fieldId ?? DEFAULT_ONLINE_FIELD,
        minutes: opts.minutes,
        periods: opts.periods,
        onDraw: opts.onDraw,
        requiresPassword: visibility === "private" && Boolean(opts.password?.trim()),
      };
      rooms = [optimistic, ...rooms];
      emit();
      client.send({
        type: "room:create",
        name,
        modeId: opts.modeId,
        nickname: opts.nickname,
        visibility: opts.visibility,
        fieldId: opts.fieldId,
        password: opts.password,
        minutes: opts.minutes,
        periods: opts.periods,
        onDraw: opts.onDraw,
      });
      return { ...optimistic, seats: { ...optimistic.seats } };
    },

    join(roomId, nickname, password) {
      client.send({ type: "room:join", roomId, nickname, password });
      return rooms.find((r) => r.id === roomId) ?? null;
    },

    takeSeat(roomId, playerId) {
      client.send({ type: "room:seat", roomId, playerId });
    },
    leave(roomId) {
      client.send({ type: "room:leave", roomId });
    },

    move(roomId, clientId, to) {
      client.send({ type: "room:move", roomId, clientId, to });
    },

    kick(roomId, clientId) {
      client.send({ type: "room:kick", roomId, clientId });
    },

    stop(roomId) {
      client.send({ type: "room:stop", roomId });
    },

    markPlaying(roomId) {
      client.send({ type: "room:start", roomId });
      const room = rooms.find((r) => r.id === roomId);
      return room ? { ...room, status: "playing" as const, seats: { ...room.seats } } : null;
    },

    get(roomId) {
      const room = rooms.find((r) => r.id === roomId);
      return room ? { ...room, seats: { ...room.seats } } : null;
    },

    dispose() {
      unsubMsg();
      listeners.clear();
    },
  };
}

export type { OnlineModeId };
